import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Select, Button, Tag, Space, Input, Modal, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getWebhookLogs } from '../../graphql/queries';
import './WebhookLogs.css';

const { Option } = Select;
const { Text } = Typography;

const SOURCE_COLORS = {
  KIE_AI: 'green',
  FAL_AI: 'blue',
  REVENUECAT: 'orange',
  SUNO_AI: 'purple',
  PANEL: 'cyan',
  OTHER: 'default'
};

const WebhookLogs = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [source, setSource] = useState(null);
  const [taskId, setTaskId] = useState('');
  const [statusCode, setStatusCode] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const { loading, data, refetch } = useQuery(getWebhookLogs, {
    variables: {
      page,
      pageSize,
      source: source || undefined,
      taskId: taskId || undefined,
      statusCode: statusCode || undefined,
    },
    fetchPolicy: 'network-only',
    pollInterval: 10000, // Auto-refresh every 10 seconds
  });

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleReset = () => {
    setSource(null);
    setTaskId('');
    setStatusCode(null);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    refetch();
  };

  const showLogDetail = (log) => {
    setSelectedLog(log);
    setModalVisible(true);
  };

  const formatJson = (obj) => {
    if (!obj) return '-';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source) => (
        <Tag color={SOURCE_COLORS[source] || 'default'}>{source}</Tag>
      )
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Task ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 150,
      ellipsis: true,
      render: (taskId) => taskId || '-'
    },
    {
      title: 'Query/Mutation',
      dataIndex: 'query',
      key: 'query',
      width: 180,
      ellipsis: true,
      render: (query) => {
        if (!query) return '-';
        try {
          const queryObj = typeof query === 'string' ? JSON.parse(query) : query;
          const operationName = queryObj.operationName || queryObj.query?.match(/(?:query|mutation)\s+(\w+)/)?.[1];
          const operationType = queryObj.query?.trim().startsWith('mutation') ? 'mutation' : 'query';
          return (
            <Space size={4}>
              <Tag color={operationType === 'mutation' ? 'orange' : 'blue'} style={{ margin: 0 }}>
                {operationType}
              </Tag>
              <Text ellipsis style={{ maxWidth: 100 }}>{operationName || 'N/A'}</Text>
            </Space>
          );
        } catch {
          return '-';
        }
      }
    },
    {
      title: 'Status',
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 80,
      render: (code) => {
        const color = code >= 500 ? 'error' : code >= 400 ? 'warning' : 'success';
        return <Tag color={color}>{code}</Tag>;
      }
    },
    {
      title: 'Response',
      dataIndex: 'response',
      key: 'response',
      width: 150,
      ellipsis: true,
      render: (response) => response || '-'
    },
    {
      title: 'Duration',
      dataIndex: 'processingMs',
      key: 'processingMs',
      width: 80,
      render: (ms) => ms ? `${ms}ms` : '-'
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      ellipsis: true,
      render: (ip) => ip || '-'
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            showLogDetail(record);
          }}
        >
          View
        </Button>
      )
    },
  ];

  return (
    <div className="webhook-logs-container">
      <div className="webhook-logs-header">
        <p className="webhook-logs-breadcrumb">Pages / Webhook Logs</p>
        <h1 className="webhook-logs-title">Webhook Logs</h1>
      </div>

      <Card className="webhook-logs-card">
        <div className="filters-container">
          <Space wrap>
            <Select
              placeholder="Source"
              allowClear
              style={{ width: 140 }}
              value={source}
              onChange={setSource}
            >
              <Option value="KIE_AI">KIE_AI</Option>
              <Option value="FAL_AI">FAL_AI</Option>
              <Option value="REVENUECAT">REVENUECAT</Option>
              <Option value="SUNO_AI">SUNO_AI</Option>
              <Option value="PANEL">PANEL</Option>
              <Option value="OTHER">OTHER</Option>
            </Select>

            <Input
              placeholder="Task ID"
              style={{ width: 200 }}
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <Select
              placeholder="Status Code"
              allowClear
              style={{ width: 140 }}
              value={statusCode}
              onChange={setStatusCode}
            >
              <Option value={200}>200 OK</Option>
              <Option value={400}>400 Bad Request</Option>
              <Option value={404}>404 Not Found</Option>
              <Option value={500}>500 Server Error</Option>
            </Select>

            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Search
            </Button>

            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>

            <Button onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data?.getWebhookLogs?.logs || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.getWebhookLogs?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} logs`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1380 }}
          size="small"
        />
      </Card>

      <Modal
        title="Webhook Log Detail"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="webhook-log-modal"
      >
        {selectedLog && (
          <div className="log-detail">
            <div className="log-detail-section">
              <Text strong>Basic Info</Text>
              <div className="log-detail-grid">
                <div><Text type="secondary">ID:</Text> {selectedLog.id}</div>
                <div><Text type="secondary">Time:</Text> {dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}</div>
                <div><Text type="secondary">Source:</Text> <Tag color={SOURCE_COLORS[selectedLog.source]}>{selectedLog.source}</Tag></div>
                <div><Text type="secondary">Endpoint:</Text> {selectedLog.endpoint}</div>
                <div><Text type="secondary">Method:</Text> {selectedLog.method}</div>
                <div><Text type="secondary">Status:</Text> <Tag color={selectedLog.statusCode >= 400 ? 'error' : 'success'}>{selectedLog.statusCode}</Tag></div>
                <div><Text type="secondary">Duration:</Text> {selectedLog.processingMs}ms</div>
                <div><Text type="secondary">IP:</Text> {selectedLog.ip || '-'}</div>
                <div><Text type="secondary">Task ID:</Text> {selectedLog.taskId || '-'}</div>
                <div>
                  <Text type="secondary">Audio ID:</Text>{' '}
                  {selectedLog.audioId ? (
                    <Button type="link" onClick={() => navigate(`/audio/${selectedLog.audioId}`)} style={{ padding: 0, height: 'auto' }}>
                      {selectedLog.audioId}
                    </Button>
                  ) : '-'}
                </div>
                <div>
                  <Text type="secondary">Vocal ID:</Text>{' '}
                  {selectedLog.vocalId ? (
                    <Button type="link" onClick={() => navigate(`/vocals/${selectedLog.vocalId}`)} style={{ padding: 0, height: 'auto' }}>
                      {selectedLog.vocalId}
                    </Button>
                  ) : '-'}
                </div>
              </div>
            </div>

            {selectedLog.query && (
              <div className="log-detail-section">
                <Text strong>GraphQL Query/Mutation</Text>
                <pre className="log-json">{formatJson(selectedLog.query)}</pre>
              </div>
            )}

            <div className="log-detail-section">
              <Text strong>Request Body</Text>
              <pre className="log-json">{formatJson(selectedLog.body)}</pre>
            </div>

            <div className="log-detail-section">
              <Text strong>Response</Text>
              <pre className="log-json">{selectedLog.response || '-'}</pre>
            </div>

            <div className="log-detail-section">
              <Text strong>Headers</Text>
              <pre className="log-json">{formatJson(selectedLog.headers)}</pre>
            </div>

            {selectedLog.errorMessage && (
              <div className="log-detail-section">
                <Text strong type="danger">Error Message</Text>
                <pre className="log-json log-error">{selectedLog.errorMessage}</pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WebhookLogs;
